export function toDaysAgo(value: Date): string {
  const diff = +new Date() - +new Date(value) // NOTE: https://github.com/Microsoft/TypeScript/issues/5710
  const num = Math.floor(diff / (86400 * 1000))
  if (num === 0) return "Today"
  return `${num}d ago`
}

export function bytesToSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes"

  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
}

export function centsToDollars(value?: number): string {
  if (value == null || value == undefined) return ""
  try {
    let prefix = "$"
    if (value < 0) prefix = "-" + prefix
    return (
      prefix +
      Math.abs(value)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    )
  } catch (error) {
    console.error(value, error)
    return value.toString()
  }
}

export function formatCurrency(amount: number): string {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  })

  return formatter.format(amount / 100)
}

export function formatCurrencyWhole(amount: number): string {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })

  return formatter.format(amount / 100)
}

export enum DateFormat {
  General,
  Short,
  Datetime,
  MonthAndDay,
}

/**
 * formatDate takes a string or Date object and returns a consistent format based on the format provided.
 * If a date string cannot be parsed a simple dash will be returned.
 * @param date string representation of a date or a Date object
 * @param type The DateFormat used to stringify the Date
 * @param asUTC whether to assume this date should be formatted based on UTC or the current users timezone
 * @returns string
 */
export function formatDate(
  date: string | Date,
  type = DateFormat.General,
  asUTC = true
): string {
  if (typeof date === "string" && isNaN(Date.parse(date))) {
    console.error(`Date string ${date} is not a valid date string.`)
    return "-"
  }

  if (typeof date === "string") {
    date = new Date(date)
  }

  const timeZone = asUTC
    ? "UTC"
    : Intl.DateTimeFormat().resolvedOptions().timeZone || undefined

  switch (type) {
    case DateFormat.General:
      return date.toLocaleDateString("en-us", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "2-digit",
        timeZone: timeZone,
      })
    case DateFormat.Short:
      return date.toLocaleDateString("en-us", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        timeZone: timeZone,
      })
    case DateFormat.Datetime:
      return new Intl.DateTimeFormat("en-US", {
        year: "2-digit",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
        timeZoneName: "short",
      }).format(date)
    case DateFormat.MonthAndDay: {
      const day = date.getDate()
      const month = new Intl.DateTimeFormat("en-US", { month: "short" }).format(
        date
      )

      return `${month} ${numberOrdinal(day)}`
    }
  }
}

/**
 * Get or append an integer ordinal
 * Ex: 1 => 1st, 2 => 2nd
 * @param n number
 * @returns string
 */
export function numberOrdinal(n: number, append = true) {
  if (n % 1 !== 0) {
    return append ? n : ""
  }
  const s = ["th", "st", "nd", "rd"]
  const v = n % 100
  const ordinal = s[(v - 20) % 10] || s[v] || s[0]
  return append ? n + ordinal : ordinal
}

export function formatTelHref(pn: string | number) {
  return pn.toString().replace(/\+\D/g, "")
}

export function formatPhoneNumber(pn: string | number) {
  // NOTE: only supports US +1 format currently
  const strip = pn.toString().replace(/\D/g, "")
  const match = strip.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)

  if (match) {
    const intlCode = match[1] ? "+1 " : ""
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("")
  }

  return ""
}
